<template>
    <MainModal :show-modal="showModal" size="md" :footer="false" :close-btn="false">
        <template #body>
            <div class="onboarding__wrap">
                <div class="onboarding__content">
                    <div class="onboarding__body"> 
                        <template v-if="step === 1">
                            <div class="onboarding__body__intro">
                                <div class="onboarding__img__wrap">
                                    <NewAppLogoSvg position="center" width="80px" />
                                </div>
                                <h2>
                                    Welcome to Ziner
                                </h2>
                                <p>We offer a collection of features and integrations designed to boost productivity.</p>

                                <div class="onboarding__body__intro__btns">
                                    <BaseButton 
                                        :label="step === 1 ? 'Continue' : 'Submit'" 
                                        :disabled="!enableBtn" 
                                        full-width
                                        size="md"
                                        class="text--sm"
                                        :loading="onboardingLoading"
                                        @submit="handleContinue"
                                    />
                                    <BaseButton 
                                        variant="ghost" 
                                        variant-style="secondary" 
                                        size="md"
                                        class="p--0 mt--20 text--sm"
                                        label="Skip" 
                                        @submit="closeOnboardingModal"
                                    />
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="mt--30 pl--15 pr--15">
                                <div class="onboarding__header">
                                    <h2>
                                        Tell us a bit more about yourself
                                    </h2>
                                    <p>
                                        Collecting this data helps us improve your experience by providing relevant tools for you. 
                                    </p>
                                </div>
                                
                                <div class="mb--20">
                                    <FormLabel title="What country are you signing up from?" label-type="form_bold" />
                                    <CustomDropdown 
                                        id="countryDropdown" v-model="form.country"
                                        size="md" :options="countries" index-label="name" placeholder="Select a country"
                                        list-type="country" @search="searchCountry" @select="val => form.country = val" 
                                    />
                                </div>
    
                                <div class="mb--20">
                                    <FormLabel title="What do you currently do?" label-type="form_bold" />
                                    <CustomDropdown 
                                        id="occupationDropdown" 
                                        v-model="form.occupation" 
                                        list-item-class="text--capitalize"
                                        size="md" 
                                        placeholder="Select an industry"
                                        btn-classes="text--capitalize" 
                                        :options="occupationList"
                                        list-type="occupation" 
                                        @search="searchOccupation" 
                                        @select="val => form.occupation = val" 
                                    />
                                </div>
                                
                                <div>
                                    <FormLabel title="How do you plan to use Ziner?" label-type="form_bold" />
                                    <div class="onboarding__usecase">
                                        <div 
                                            v-for="(item, index) in zinerUseCases" :key="item" 
                                            class="onboarding__usecase__item" :class="{ 'active': form.use_cases.includes(item) }"
                                        >
                                            <div class="form-check mb--0 form-check-inline w-100 min-h-auto">
                                                <input 
                                                    :id="`inlineCheckbox${index}`" v-model="form.use_cases" 
                                                    class="form-check-input" type="checkbox" :value="item"
                                                >
                                                <label class="form-check-label mb--0 text-gray-400" :for="`inlineCheckbox${index}`">{{ item }}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="w-100 flex align-items-center justify-content-end mt--20 gap-1">
                                <BaseButton 
                                    variant="ghost" 
                                    variant-style="secondary" 
                                    label="Skip" 
                                    medium
                                    @submit="closeOnboardingModal"
                                />
                                <BaseButton 
                                    :label="step === 1 ? 'Continue' : 'Submit'" 
                                    :disabled="!enableBtn" 
                                    :loading="onboardingLoading"
                                    @submit="handleContinue"
                                />
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </template>
    </MainModal>
</template>

<script>
import BaseButton from "../buttons/BaseButton.vue";
import MainModal from "./MainModal.vue";
import CustomDropdown from "../dropdowns/CustomDropdown.vue";
import { occupationList, zinerUseCases } from "@/utils/dummy";
import countryList from "@/assets/js/countrycodes.json";
import FormLabel from "../forms/FormLabel.vue";
import NewAppLogoSvg from "../logos/NewAppLogoSvg.vue";

export default {
    name: "OnboardingModal",

    components: {
        MainModal, 
        CustomDropdown,
        BaseButton, 
        FormLabel,
        NewAppLogoSvg,
    },

    props: {
        showModal: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            occupationList,
            countries: countryList,
            zinerUseCases,
            step: 1,
            form: {
                country: "",
                occupation: "",
                use_cases: [],
                bio: "",
            },
            onboardingLoading: false,
        };
    },

    computed: {
        enableBtn() {
            if (this.step === 1) return true;

            return Object.values(this.form).some((value) => {                
                if (Array.isArray(value)) {                    
                    return value.length;
                }
                return value;
            });
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },
    },

    methods: {
        searchOccupation(val) {
            this.occupationList = occupationList.filter((item) => {
                return item.toLowerCase().includes(val?.toLowerCase());
            });
        },

        searchCountry(val) {
            this.countries = countryList.filter((item) => {
                return item.name.toLowerCase().includes(val?.toLowerCase());
            });
        },

        async handleContinue() {
            if (this.step === 1) {
                this.step = 2;
                return;
            } else {
                await this.updateUserInformation();
            }
        },

        closeOnboardingModal() {
            this.$store.dispatch("app/toggleNewUser", false);
        },

        async updateUserInformation() {
            try {
                this.onboardingLoading = true;

                const payload = {
                    occupation: this.form.occupation || null,
                    country: this.form.country.name || null,
                    bio: this.form.bio || null,
                    // use_cases: this.form.use_cases, // future: feed into data pipeline
                };

                delete payload.userAgent;

                await this.$store.dispatch("auth/updateProfile", payload);
                this.onboardingLoading = false;
                this.$toast.success("Thank you!");
                this.$store.dispatch("app/toggleNewUser", false);
            } catch (error) {
                this.onboardingLoading = false;
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .onboarding {
        &__img__wrap {
            width: 100%;
            display: flex;
            justify-content: center;
            margin-bottom: 30px;
        }

        &__wrap {
            min-height: 550px;
            min-height: 350px;
            display: flex;
            flex-direction: column;
        }

        &__content {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            height: calc(100% - 50px);
            flex: 2;
        }

        &__header {
            margin: auto;
            text-align: center;
            max-width: 75%;
            margin-bottom: 2.5rem;

            h2 {
                font-size: 28px;
                font-weight: 600;
                color: #1a202c;
                margin-bottom: 12px;
                letter-spacing: -0.5px;
            }
           
            p {
                color: #718096;
                line-height: 1.6;
                font-weight: 400;
            }
        }

        &__body {
            &__intro {
                padding: 3rem 0;
                margin: auto;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;

                h2 {
                    font-size: 2rem;
                    font-weight: 600;
                    color: #09090B;
                    margin-bottom: 0.5rem;
                }

                p {
                    color: var(--grey-600);
                    font-size: 0.875rem;
                    text-align: center;
                    max-width: 50%;
                }

                &__btns {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-direction: column;
                    margin-top: 4rem;
                    max-width: 50%;
                    width: fit-content;
                }   
            }
        }

        &__usecase {
            display: flex;
            flex-wrap: wrap;
            gap: 10px 5px;
            margin-top: 10px;

            &__item {
                display: flex;
                border: 1px solid #cbd5e1;
                padding: 3px 10px;
                background: #fff;
                font-size: 0.875rem;
                border-radius: 6px;     

                &.active {
                    border: 1px solid #289CA9;
                }

                .form-check-label {
                    min-width: 80%;
                }
            }
        }

        &__progress {
            &__item {
                width: 20px;
            }
        }
    }
</style>